import { useCallback, useContext } from 'react';

import cardTypeCheckers from '../../../../../../types/cardTypeCheckers';
import ReportGadgetsContext from '../../../../../../contexts/ReportGadgetsContext';
import ReportsContext from '../../../../../../contexts/ReportsContext';

import { FieldUsageEntities } from '../types';

const useGetFieldReportUsage = () => {
  const { reportGadgetsLookup } = useContext(ReportGadgetsContext);
  const { allReports } = useContext(ReportsContext);

  const getFieldReportUsage = useCallback(
    (usage: FieldUsageEntities): ReportType[] => {
      if (!usage) return [];

      // Reports that explicitly reference the field via report filters
      const explicitReportIds = new Set<string>();
      usage.reportFilters?.forEach((r) => r?.id && explicitReportIds.add(r.id));

      // Chart Definitions used in reports (Simple Grids and their sorts apply to reports too)
      const chartDefIds = new Set<string>();
      usage.simpleGrids.forEach((d) => d?.id && chartDefIds.add(d.id));
      usage.simpleGridSorts.forEach((d) => d?.id && chartDefIds.add(d.id));
      usage.gadgetDimensions.forEach((d) => d?.id && chartDefIds.add(d.id));

      const matchedReportIds = new Set<string>(explicitReportIds);

      allReports.forEach((r) => {
        const cards = r.canvas?.cards || [];
        const hasMatch = cards.some((card) => {
          if (cardTypeCheckers.isChartDefinition(card)) {
            return chartDefIds.has(card.content.chartDefinitionId);
          } else if (cardTypeCheckers.isReportGadget(card)) {
            const reportGadget =
              reportGadgetsLookup[card.content.reportGadgetId];
            return reportGadget?.chartDef?.id
              ? chartDefIds.has(reportGadget.chartDef.id)
              : false;
          }
          return false;
        });

        if (hasMatch) matchedReportIds.add(r.id);
      });

      // Return ReportType objects in the same order as allReports
      return allReports.filter((r) => matchedReportIds.has(r.id));
    },
    [allReports, reportGadgetsLookup],
  );
  return getFieldReportUsage;
};

export default useGetFieldReportUsage;
